/**
 * OnlineMatchCanvas.jsx
 * 
 * DOM/SVG-based online multiplayer match canvas.
 * Uses the new rendering system with effects, combos, and screen shake.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react'
import { useApp } from '../context/AppContext'
import { useGameInput, controlMap } from '../hooks/useGameInput'
import {
  buildPlayableStage,
  makeFighter,
  updateFighterTimers,
  applyControls,
  updatePhysics,
  checkStocks,
  updateCamera,
} from '../game/engine'
import { NeonCity, VolcanicArena, AncientTemple, SpaceStation, Battlefield } from '../components/stages'
import { GameContainer, FighterSvg, GameHud, ComboDisplay, EffectsSystem } from './game'
import { API_BASE } from '../config/api'

const GAME_WIDTH = 1200
const GAME_HEIGHT = 600

const stageComponents = {
  neonPier: NeonCity,
  reactorCore: VolcanicArena,
  skySpire: AncientTemple,
  ironRing: SpaceStation,
  battlefield: Battlefield,
}

export function OnlineMatchCanvas({
  match,
  playerCharacter,
  playerSlot = 'p1',
  stagePresets,
  onMatchEnd,
  isActive = true,
}) {
  const { state } = useApp()
  const currentUser = state.users.find(u => u.id === state.currentUserId)
  
  // DOM/SVG refs
  const gameContainerRef = useRef(null)
  const leftFighterRef = useRef(null)
  const rightFighterRef = useRef(null)
  const comboRef = useRef(null)
  const effectsRef = useRef(null)
  
  const animationRef = useRef(null)
  const lastTickRef = useRef(0)
  const gameStateRef = useRef(null)
  const wsRef = useRef(null)
  const remoteInputRef = useRef({ left: false, right: false, up: false, attack: false, special: false })
  const prevRemoteInputRef = useRef({ left: false, right: false, up: false, attack: false, special: false })
  const frameRef = useRef(0)
  const matchDataRef = useRef(null)
  const pendingHitsRef = useRef(new Set())
  const lastHitSentTimeRef = useRef({ p1: 0, p2: 0 })
  const lastStockLostTimeRef = useRef({ p1: 0, p2: 0 })
  
  const [uiState, setUiState] = useState({
    running: false,
    winner: null,
    initialized: false,
    connecting: true,
    error: null,
    gameTime: 0,
  })

  const { isPressed, consumePressedOnce } = useGameInput(isActive)

  // Initialize game state
  if (!gameStateRef.current && match) {
    const stagePresetId = match?.stageId || 'battlefield'
    let stagePreset = stagePresets?.[stagePresetId]
    if (!stagePreset) {
      stagePreset = stagePresets?.battlefield || {
        id: 'battlefield',
        name: 'Battlefield',
        left: 220,
        right: 980,
        floor: 450,
        ceiling: 0,
        blastBottom: 700,
        spawnLeft: 400,
        spawnRight: 800,
        spawnY: 350,
        platforms: [],
      }
    }
    gameStateRef.current = {
      running: false,
      left: null,
      right: null,
      lastTick: 0,
      winner: null,
      mode: 'online',
      matchEndAt: 0,
      stage: buildPlayableStage(stagePreset),
      camera: { x: 0, y: 0 },
      gameTime: 0,
    }
  }

  const gameState = gameStateRef.current

  // WebSocket connection
  useEffect(() => {
    if (!match?.id) return

    const connect = () => {
      const params = new URLSearchParams()
      if (currentUser?.id) params.set('userId', currentUser.id)
      if (currentUser?.name) params.set('userName', currentUser.name)
      if (playerCharacter) params.set('character', encodeURIComponent(JSON.stringify(playerCharacter)))
      const queryString = params.toString()
      
      const wsUrl = API_BASE 
        ? `${API_BASE.replace(/^http/, 'ws')}/api/matches/${match.id}/ws${queryString ? '?' + queryString : ''}`
        : `wss://${window.location.host}/api/matches/${match.id}/ws${queryString ? '?' + queryString : ''}`
      
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setUiState(prev => ({ ...prev, connecting: false }))
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'match-starting':
              // Store match data including opponent character
              matchDataRef.current = data
              
              // Start local countdown from the server's countdown value
              const startCount = data.countdown || 3
              setUiState(prev => ({ ...prev, countdown: startCount }))
              
              // Local countdown timer
              let count = startCount
              const countdownInterval = setInterval(() => {
                count--
                if (count > 0) {
                  setUiState(prev => ({ ...prev, countdown: count }))
                } else {
                  clearInterval(countdownInterval)
                }
              }, 1000)
              break
            case 'match-started':
              console.log(`[Match] ${playerSlot} received match-started`)
              startGame()
              break
            case 'player-input':
              if (data.playerSlot !== playerSlot) {
                console.log(`[Input] Received from ${data.playerSlot}:`, data.input)
                remoteInputRef.current = data.input
              }
              break
            case 'sync':
              // Apply position correction from server
              if (data.positions) {
                const remotePos = playerSlot === 'p1' ? data.positions.p2 : data.positions.p1
                const remoteFighter = playerSlot === 'p1' ? gameState?.right : gameState?.left
                
                // Smoothly correct remote fighter position
                if (remoteFighter && remotePos) {
                  const dx = remotePos.x - remoteFighter.x
                  const dy = remotePos.y - remoteFighter.y
                  // Only correct if drift is significant (>5 pixels)
                  if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
                    remoteFighter.x += dx * 0.3 // Smooth correction
                    remoteFighter.y += dy * 0.3
                    remoteFighter.vx = remotePos.vx || 0
                    remoteFighter.vy = remotePos.vy || 0
                  }
                }
              }
              
              // Sync HP from server (server is authoritative)
              if (data.hp && gameState?.left && gameState?.right) {
                gameState.left.hp = data.hp.p1 || 0
                gameState.right.hp = data.hp.p2 || 0
              }
              
              // Sync stocks from server
              if (data.stocks && gameState?.left && gameState?.right) {
                gameState.left.stocks = data.stocks.p1 ?? 3
                gameState.right.stocks = data.stocks.p2 ?? 3
              }
              break
            case 'player-hit':
              // Remote player hit someone - apply damage
              if (data.attackerSlot && data.defenderSlot && data.damage) {
                const hitId = `${data.attackerSlot}-${data.defenderSlot}-${data.frame}-${data.damage}`
                
                // Skip if we already processed this hit
                if (pendingHitsRef.current.has(hitId)) {
                  break
                }
                pendingHitsRef.current.add(hitId)
                
                // Clean up old hit IDs (keep last 100)
                if (pendingHitsRef.current.size > 100) {
                  const iter = pendingHitsRef.current.values()
                  for (let i = 0; i < 20; i++) {
                    const oldHit = iter.next().value
                    pendingHitsRef.current.delete(oldHit)
                  }
                }
                
                // Apply damage to the defender
                const defender = data.defenderSlot === 'p1' ? gameState?.left : gameState?.right
                if (defender && !gameState.winner) {
                  defender.hp += data.damage
                  defender.invuln = 0.45
                  defender.hitstun = data.isSpecial ? 0.28 : 0.18
                  
                  // Apply knockback based on attacker's facing (approximate)
                  const attacker = data.attackerSlot === 'p1' ? gameState?.left : gameState?.right
                  const facing = attacker ? attacker.facing : (data.defenderSlot === 'p1' ? 1 : -1)
                  defender.vx += facing * (4.5 + defender.hp * 0.06 + 5 * 0.32)
                  defender.vy -= 165 + defender.hp * 0.42
                  defender.statusTimer = 0.22
                }
              }
              break
            case 'stock-lost':
              // A player lost a stock
              if (data.playerSlot && gameState) {
                const fighter = data.playerSlot === 'p1' ? gameState.left : gameState.right
                if (fighter) {
                  fighter.stocks = data.stocks
                  fighter.hp = 0
                  fighter.x = fighter.spawnX
                  fighter.y = fighter.spawnY
                  fighter.vx = 0
                  fighter.vy = 0
                  fighter.hitstun = 0
                  fighter.onGround = false
                  fighter.jumpCount = 0
                  fighter.invuln = 1.6
                  fighter.comboText = 'Respawn'
                  fighter.statusTimer = 0.75
                }
              }
              break
            case 'match-ended':
              handleMatchEnd(data.winner, data.reason)
              break
            case 'player-left':
              handleMatchEnd(playerSlot, 'forfeit')
              break
          }
        } catch (err) {
          console.error('[Match] Parse error:', err)
        }
      }

      ws.onerror = (err) => {
        console.error('[Match] Connection error')
        setUiState(prev => ({ ...prev, error: 'Connection error' }))
      }

      ws.onclose = () => {
        if (uiState.running && !uiState.winner) {
          setUiState(prev => ({ ...prev, error: 'Connection lost' }))
        }
      }
    }

    connect()

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [match?.id])

  // Send input to server
  const sendInput = useCallback((input) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'input',
        input,
        frame: frameRef.current,
      }))
    }
  }, [])

  // Send position to server for sync
  const sendPosition = useCallback((position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'position',
        playerSlot,
        position,
      }))
    }
  }, [playerSlot])

  // Send hit to server for multiplayer sync
  const sendHit = useCallback((defenderSlot, damage, isSpecial) => {
    console.log(`[sendHit] Attempting to send: ${playerSlot} -> ${defenderSlot}, damage: ${damage}, WS state: ${wsRef.current?.readyState}`)
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'hit',
        attackerSlot: playerSlot,
        defenderSlot,
        damage,
        isSpecial,
        frame: frameRef.current,
      }
      wsRef.current.send(JSON.stringify(message))
      console.log(`[sendHit] SENT:`, message)
    } else {
      console.log(`[sendHit] FAILED: WebSocket not open (state: ${wsRef.current?.readyState})`)
    }
  }, [playerSlot])

  // Send stock loss to server
  const sendStockLost = useCallback((lostPlayerSlot, stocks) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'stock-lost',
        playerSlot: lostPlayerSlot,
        stocks,
        frame: frameRef.current,
      }))
    }
  }, [])

  const startGame = () => {
    if (!gameState) {
      return
    }

    const stagePresetId = match.stageId || 'battlefield'
    let stagePreset = stagePresets?.[stagePresetId]
    if (!stagePreset) {
      stagePreset = stagePresets?.battlefield || {
        id: 'battlefield',
        name: 'Battlefield',
        left: 220,
        right: 980,
        floor: 450,
        ceiling: 0,
        blastBottom: 700,
        spawnLeft: 400,
        spawnRight: 800,
        spawnY: 350,
        platforms: [],
      }
    }
    
    // player1 = host, player2 = guest
    const wsData = matchDataRef.current
    const p1Char = wsData?.hostCharacter || state.characters.find(c => c.id === match?.player1CharacterId) || playerCharacter
    const p2Char = wsData?.guestCharacter || state.characters.find(c => c.id === match?.player2CharacterId) || { name: 'Player 2', color: '#f0f', speed: 1, power: 1, defense: 1 }

    try {
      gameState.stage = buildPlayableStage(stagePreset)
      // P1 (host) is always on the left, P2 (guest) is always on the right
      gameState.left = makeFighter(p1Char, gameState.stage.spawnLeft, gameState.stage, 'p1')
      gameState.right = makeFighter(p2Char, gameState.stage.spawnRight, gameState.stage, 'p2')
      gameState.mode = 'online'
      gameState.winner = null
      gameState.running = true
      gameState.lastTick = performance.now()
      gameState.matchEndAt = 0
      gameState.gameTime = 0
      frameRef.current = 0
      
      updateCamera(gameState.camera, [gameState.left, gameState.right], gameState.stage, 1, true)
      
      // Clear effects
      effectsRef.current?.clear()
      comboRef.current?.clear()
      
      setUiState({
        running: true,
        winner: null,
        initialized: true,
        connecting: false,
        error: null,
        countdown: null,
        gameTime: 0,
      })
    } catch (err) {
      setUiState(prev => ({ ...prev, error: 'Failed to start game' }))
    }
  }

  const handleMatchEnd = (winner, reason = 'ko') => {
    if (!gameState || gameState.winner) return
    
    gameState.running = false
    gameState.winner = winner
    gameState.matchEndAt = performance.now()
    
    setUiState(prev => ({
      ...prev,
      running: false,
      winner,
      gameTime: gameState.gameTime,
    }))

    // Only send match-end if we're the winner's side (to avoid duplicate messages)
    // or if the match ended due to forfeit/disconnect
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'match-end',
        winner,
        reason,
      }))
    }

    if (onMatchEnd) {
      onMatchEnd(winner)
    }
  }

  const updateGame = useCallback((dt) => {
    if (!gameState || !gameState.running) return

    const fighters = [gameState.left, gameState.right]
    const localFighter = playerSlot === 'p1' ? gameState.left : gameState.right
    const remoteFighter = playerSlot === 'p1' ? gameState.right : gameState.left

    // Track damage before combat resolution for visual effects
    const leftHpBefore = gameState.left.hp
    const rightHpBefore = gameState.right.hp

    // Online mode: P1 uses WASD, P2 uses arrow keys
    // Both online players use WASD controls (p1 mapping)
    const localControls = controlMap.p1
    
    const localWasPressed = (keyCode) => {
      const pressedOnce = consumePressedOnce()
      return pressedOnce.has(keyCode)
    }
    applyControls(localFighter, localControls, isPressed, localWasPressed, dt)
    
    if (remoteFighter) {
      const remoteInput = remoteInputRef.current
      const prevRemoteInput = prevRemoteInputRef.current
      const remoteIsPressed = (key) => !!remoteInput[key]
      const remoteWasPressed = (key) => {
        return !!remoteInput[key] && !prevRemoteInput[key]
      }
      const remoteKeys = { left: 'left', right: 'right', jump: 'up', attack: 'attack', special: 'special', dash: 'dash' }
      applyControls(remoteFighter, remoteKeys, remoteIsPressed, remoteWasPressed, dt)
      
      prevRemoteInputRef.current = { ...remoteInput }
    }

    fighters.forEach((fighter) => {
      updateFighterTimers(fighter, dt)
      updatePhysics(fighter, gameState.stage, dt)
    })
    
    // Check for hits and trigger visual effects
    const leftAfter = gameState.left
    const rightAfter = gameState.right

    // Left fighter hit right fighter
    if (rightAfter.hp > rightHpBefore) {
      // Spawn hit effect at right fighter position
      effectsRef.current?.spawnHitParticles(
        rightAfter.x + rightAfter.width / 2,
        rightAfter.y + rightAfter.height / 2,
        8,
        '#ffffff',
        1 + (leftAfter.hp / 100)
      )
      effectsRef.current?.spawnImpact(
        rightAfter.x + rightAfter.width / 2,
        rightAfter.y + rightAfter.height / 2,
        '#ff6b6b',
        40 + leftAfter.hp / 3
      )
      
      // Trigger hit reaction on right fighter
      rightFighterRef.current?.triggerHit()
      
      // Screen shake
      gameContainerRef.current?.shake(Math.min(8, 3 + leftAfter.hp / 30), 150)
    }

    // Right fighter hit left fighter
    if (leftAfter.hp > leftHpBefore) {
      // Spawn hit effect at left fighter position
      effectsRef.current?.spawnHitParticles(
        leftAfter.x + leftAfter.width / 2,
        leftAfter.y + leftAfter.height / 2,
        8,
        '#ffffff',
        1 + (rightAfter.hp / 100)
      )
      effectsRef.current?.spawnImpact(
        leftAfter.x + leftAfter.width / 2,
        leftAfter.y + leftAfter.height / 2,
        '#ff6b6b',
        40 + rightAfter.hp / 3
      )
      
      // Trigger hit reaction on left fighter
      leftFighterRef.current?.triggerHit()
      
      // Screen shake
      gameContainerRef.current?.shake(Math.min(8, 3 + rightAfter.hp / 30), 150)
    }

    // Multiplayer-aware combat resolution with visual effects
    // Check for hits from both fighters and send to server
    // Both clients detect hits and send them - server deduplicates
    const resolveMultiplayerCombat = (attacker, defender) => {
      if (!attacker || !defender) return
      
      const attackerSlot = attacker === gameState.left ? 'p1' : 'p2'
      
      // Match engine's detection window: attackCooldown > 0.2
      if (attacker.attackCooldown <= 0.2) return
      
      const ax = attacker.x + attacker.width / 2
      const ay = attacker.y + attacker.height / 2
      const dx = Math.abs(ax - (defender.x + defender.width / 2))
      const dy = Math.abs(ay - (defender.y + defender.height / 2))

      if (dx < 58 && dy < 42 && defender.invuln <= 0) {
        const damage = attacker.comboText === attacker.specialName ? 16 : 8
        const isSpecial = attacker.comboText === attacker.specialName
        const defenderSlot = defender === gameState.left ? 'p1' : 'p2'
        
        // Debounce: only send hit once per ~300ms per attacker
        const now = performance.now()
        const lastSent = lastHitSentTimeRef.current[attackerSlot]
        
        if (now - lastSent > 300) { // 300ms cooldown between hit sends per player
          lastHitSentTimeRef.current[attackerSlot] = now
          
          // Send hit to server
          sendHit(defenderSlot, damage, isSpecial)
        }
      }
    }

    resolveMultiplayerCombat(gameState.left, gameState.right)
    resolveMultiplayerCombat(gameState.right, gameState.left)

    // Check stocks and sync with server
    if (gameState.left && gameState.right && gameState.stage) {
      const result = checkStocks([gameState.left, gameState.right], gameState.stage)
      if (result.gameOver && result.winner && !gameState.winner) {
        const winnerSlot = result.winner === gameState.left ? 'p1' : 'p2'
        handleMatchEnd(winnerSlot, 'ko')
        return
      }
      
      // Send stock loss events to server (for sync)
      if (gameState.left.comboText === 'Respawn' && gameState.left.invuln > 1.5) {
        const now = Date.now()
        if (now - (lastStockLostTimeRef.current.p1 || 0) > 1000) {
          sendStockLost('p1', gameState.left.stocks)
          lastStockLostTimeRef.current.p1 = now
        }
      }
      if (gameState.right.comboText === 'Respawn' && gameState.right.invuln > 1.5) {
        const now = Date.now()
        if (now - (lastStockLostTimeRef.current.p2 || 0) > 1000) {
          sendStockLost('p2', gameState.right.stocks)
          lastStockLostTimeRef.current.p2 = now
        }
      }
    }

    updateCamera(gameState.camera, fighters, gameState.stage, dt)
    
    // Update DOM positions (60fps direct manipulation)
    if (gameState.left && leftFighterRef.current) {
      leftFighterRef.current.setPosition(gameState.left.x, gameState.left.y)
      leftFighterRef.current.setFacing(gameState.left.facing === -1)
    }
    if (gameState.right && rightFighterRef.current) {
      rightFighterRef.current.setPosition(gameState.right.x, gameState.right.y)
      rightFighterRef.current.setFacing(gameState.right.facing === -1)
    }

    // Update camera transform
    if (gameContainerRef.current) {
      gameContainerRef.current.setCamera(gameState.camera.x, gameState.camera.y, 1)
    }
    
    // Update game time
    gameState.gameTime += dt

    frameRef.current++

    if (frameRef.current % 3 === 0) {
      const input = {
        left: isPressed(localControls.left),
        right: isPressed(localControls.right),
        up: isPressed(localControls.jump),
        attack: isPressed(localControls.attack),
        special: isPressed(localControls.special),
        dash: isPressed(localControls.dash),
      }
      sendInput(input)
    }
    
    // Send position sync every 10 frames (~167ms)
    if (frameRef.current % 10 === 0) {
      sendPosition({
        x: localFighter.x,
        y: localFighter.y,
        vx: localFighter.vx,
        vy: localFighter.vy,
      })
    }
  }, [gameState, isPressed, consumePressedOnce, playerSlot, sendInput, sendPosition, sendHit, sendStockLost])

  // Game loop
  useEffect(() => {
    console.log(`[GameLoop] isActive=${isActive}, running=${uiState.running}, playerSlot=${playerSlot}`)
    if (!isActive || !uiState.running) return

    console.log(`[GameLoop] Starting for ${playerSlot}`)
    let lastTime = performance.now()

    const gameLoop = (currentTime) => {
      const dt = Math.min((currentTime - lastTime) / 1000, 0.1)
      lastTime = currentTime

      updateGame(dt)
      
      if (gameState.running) {
        animationRef.current = requestAnimationFrame(gameLoop)
      }
    }

    animationRef.current = requestAnimationFrame(gameLoop)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isActive, uiState.running, updateGame])

  // Handle window blur/focus
  useEffect(() => {
    const handleBlur = () => {
      if (gameState) {
        gameState.lastTick = performance.now()
      }
    }
    window.addEventListener('blur', handleBlur)
    return () => window.removeEventListener('blur', handleBlur)
  }, [])

  if (uiState.connecting) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Connecting to match...</p>
        </div>
      </div>
    )
  }

  if (uiState.error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-red-400 mb-4">⚠️ {uiState.error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (uiState.countdown) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-6xl font-black text-cyan-400 animate-pulse">
            {uiState.countdown}
          </div>
          <p className="text-gray-400 mt-4">Match starting...</p>
        </div>
      </div>
    )
  }

  const StageBackground = stageComponents[match?.stageId || 'battlefield'] || Battlefield
  
  // Get characters - left is always P1, right is always P2
  const wsData = matchDataRef.current
  const p1Character = wsData?.hostCharacter || state.characters.find(c => c.id === match?.player1CharacterId) || playerCharacter
  const p2Character = wsData?.guestCharacter || state.characters.find(c => c.id === match?.player2CharacterId) || { name: 'Player 2', archetype: 'shadowninja' }
  
  // For fighter rendering - left is P1, right is P2 regardless of local player
  // This ensures both players see the same characters in the same positions
  const leftChar = p1Character
  const rightChar = p2Character

  return (
    <>
      {/* Game Container - everything contained within */}
      <GameContainer
        ref={gameContainerRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        backgroundComponent={StageBackground}
        backgroundProps={{ animate: true }}
        overlayChildren={
          <>
            {/* Effects layer */}
            <EffectsSystem ref={effectsRef} width={GAME_WIDTH} height={GAME_HEIGHT} />
            <ComboDisplay ref={comboRef} width={GAME_WIDTH} height={GAME_HEIGHT} />
            
            {/* HUD layer - left is always P1, right is always P2 */}
            <GameHud
              gameState={gameState}
              leftCharacter={p1Character}
              rightCharacter={p2Character}
              gameTime={uiState.gameTime}
              isLocalPlayerLeft={playerSlot === 'p1'}
            />
            
            {/* Waiting overlay */}
            {!uiState.running && !uiState.winner && !uiState.countdown && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: GAME_WIDTH,
                  height: GAME_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  pointerEvents: 'auto',
                }}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-4">Waiting for match to start...</div>
                  <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              </div>
            )}
            
            {/* Winner display */}
            {uiState.winner && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: GAME_WIDTH,
                  height: GAME_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div className="text-6xl font-black text-white drop-shadow-lg animate-bounce">
                  {uiState.winner === playerSlot ? 'You Win!' : 'You Lose!'}
                </div>
              </div>
            )}
          </>
        }
      >
        {/* Fighters in world layer - left is P1, right is P2 */}
        {gameState?.left && (
          <FighterSvg
            ref={leftFighterRef}
            fighter={gameState.left}
            characterId={leftChar?.archetype}
            isLocal={playerSlot === 'p1'}
          />
        )}
        {gameState?.right && (
          <FighterSvg
            ref={rightFighterRef}
            fighter={gameState.right}
            characterId={rightChar?.archetype}
            isLocal={playerSlot === 'p2'}
          />
        )}
      </GameContainer>
    </>
  )
}
